import { useQuery } from '@tanstack/react-query';
import type { PeriodInput, Week } from 'chery-contracts';
import { weeksBetween } from 'chery-kpi-engine';
import { useMemo, useState } from 'react';
import { orpc } from '../../apis/orpc-query';
import { Select } from '../../components/ui/Select';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import type { CustomRange } from '../reports/reports-display';
import { weekDateLabel } from '../tracker/tracker-display';
import { BOARD_PERIOD_CHOICES, type BoardPeriodChoice, boardPeriodFor, DEFAULT_BOARD_PERIOD } from './board-display';

export interface BoardPeriod {
  choice: BoardPeriodChoice;
  custom: CustomRange | null;
  /** The shared `period` shape to send; `null` for Custom until a valid From/To exists. */
  period: PeriodInput | null;
  rangeWarning: string | null;
  clearWarning: () => void;
  weeks: readonly Week[];
  /** `resolved` = the range the page currently shows, used as the starting point of a Custom range. */
  onChoice: (next: BoardPeriodChoice, resolved: CustomRange | null) => void;
  onCustom: (fromWeekId: string, toWeekId: string) => void;
}

/**
 * The board's period state (plan §7): Year to date by default, the Reports choices, and a checked Custom range — one
 * hook for the stage sheets and the Products page, so both send the same `period` shape.
 */
export const useBoardPeriod = (): BoardPeriod => {
  const [choice, setChoice] = useState<BoardPeriodChoice>(DEFAULT_BOARD_PERIOD);
  const [custom, setCustom] = useState<CustomRange | null>(null);
  const [rangeWarning, setRangeWarning] = useState<string | null>(null);

  const weeksQuery = useQuery({ ...orpc.weeks.list.queryOptions({ input: {} }), enabled: choice === 'CUSTOM' });
  const weeks = useMemo(() => weeksQuery.data?.items ?? [], [weeksQuery.data]);

  return {
    choice,
    custom,
    period: boardPeriodFor(choice, custom),
    rangeWarning,
    clearWarning: () => setRangeWarning(null),
    weeks,
    onChoice: (next, resolved) => {
      setRangeWarning(null);
      setChoice(next);

      if (next === 'CUSTOM' && custom === null && resolved) {
        setCustom(resolved);
      }
    },
    onCustom: (fromWeekId, toWeekId) => {
      const checked = weeksBetween(weeks, fromWeekId, toWeekId);

      if (!checked.ok) {
        setRangeWarning(checked.message);

        return;
      }

      setRangeWarning(null);
      setCustom({ fromWeekId, toWeekId });
    },
  };
};

export interface BoardPeriodControlsProps {
  state: BoardPeriod;
  /** The range currently shown, to seed a Custom range. */
  resolved: CustomRange | null;
}

/** The period select and, for Custom, the From → To week selects. */
export const BoardPeriodControls = ({ state, resolved }: BoardPeriodControlsProps) => {
  const { t } = useI18nTranslation();
  const { choice, custom, rangeWarning } = state;
  const weekOptions = state.weeks.map((week) => ({
    value: week.id,
    label: `${weekDateLabel(week.weekDate)} · W${String(week.weekNumber).padStart(2, '0')}`,
  }));

  return (
    <>
      <Select
        aria-label={t('board.sheets.period')}
        data-board-period
        value={choice}
        options={BOARD_PERIOD_CHOICES.map((each) => ({ value: each, label: t(`board.periods.${each}`) }))}
        onChange={(event) => state.onChoice(event.target.value as BoardPeriodChoice, resolved)}
        className="h-8 w-44 text-xs"
      />
      {choice === 'CUSTOM' && custom && weekOptions.length > 0 ? (
        <>
          <Select
            aria-label={t('reports.from')}
            value={custom.fromWeekId}
            options={weekOptions}
            hasError={rangeWarning !== null}
            onChange={(event) => state.onCustom(event.target.value, custom.toWeekId)}
            className="h-8 w-44 text-xs"
          />
          <span aria-hidden>→</span>
          <Select
            aria-label={t('reports.to')}
            value={custom.toWeekId}
            options={weekOptions}
            hasError={rangeWarning !== null}
            onChange={(event) => state.onCustom(custom.fromWeekId, event.target.value)}
            className="h-8 w-44 text-xs"
          />
        </>
      ) : null}
    </>
  );
};
